export type ThemeFamily = 'material' | 'generic' | 'fluent';
export type ThemeMode = 'light' | 'dark';
export type MaterialAccent = 'blue' | 'lime' | 'orange' | 'purple' | 'teal' | 'hvg';
export type FluentVariant = 'blue' | 'saas' | 'red-storm';
export type GenericPalette =
  | 'light'
  | 'dark'
  | 'carmine'
  | 'softblue'
  | 'darkmoon'
  | 'darkviolet'
  | 'greenmist'
  | 'contrast';

export interface ThemePreferences {
  family: ThemeFamily;
  materialAccent: MaterialAccent;
  genericPalette: GenericPalette;
  genericCompact: boolean;
  fluentVariant: FluentVariant;
  fluentCompact: boolean;
  mode: ThemeMode;
}

export interface ThemeCatalogEntry {
  themeId: string;
  cssFile: string;
  family: ThemeFamily;
  label: string;
  swatchColor: string;
  supportsToolbarMode: boolean;
}

export const DEFAULT_THEME_ID = 'material.blue.light';
export const DEFAULT_THEME_CSS = 'dx.material.blue.light.css';

export const MATERIAL_ACCENTS: readonly MaterialAccent[] = [
  'blue',
  'lime',
  'orange',
  'purple',
  'teal',
  'hvg'
] as const;

export const FLUENT_VARIANTS: readonly FluentVariant[] = ['blue', 'saas', 'red-storm'] as const;

/** Custom ThemeBuilder exports — not in devextreme npm package. */
export const CUSTOM_THEME_CSS_FILES = [
  'dx.material.hvg.light.css',
  'dx.material.hvg.dark.css',
  'dx.fluent.red-storm.dark.css'
] as const;

export const GENERIC_PALETTES: readonly GenericPalette[] = [
  'light',
  'dark',
  'carmine',
  'softblue',
  'darkmoon',
  'darkviolet',
  'greenmist',
  'contrast'
] as const;

const MATERIAL_LABELS: Record<MaterialAccent, string> = {
  blue: 'Blue',
  lime: 'Lime',
  orange: 'Orange',
  purple: 'Purple',
  teal: 'Teal',
  hvg: 'HVG'
};

const MATERIAL_SWATCHES: Record<MaterialAccent, string> = {
  blue: '#03a9f4',
  lime: '#cddc39',
  orange: '#ff5722',
  purple: '#9c27b0',
  teal: '#009688',
  hvg: '#395f97'
};

const FLUENT_LABELS: Record<FluentVariant, string> = {
  blue: 'Blue',
  saas: 'SaaS',
  'red-storm': 'Storm Red'
};

const FLUENT_SWATCHES: Record<FluentVariant, string> = {
  blue: '#0f6cbd',
  saas: '#242424',
  'red-storm': '#e00d0d'
};

const GENERIC_LABELS: Record<GenericPalette, string> = {
  light: 'Light',
  dark: 'Dark',
  carmine: 'Carmine',
  softblue: 'Soft Blue',
  darkmoon: 'Dark Moon',
  darkviolet: 'Dark Violet',
  greenmist: 'Green Mist',
  contrast: 'Contrast'
};

const GENERIC_SWATCHES: Record<GenericPalette, string> = {
  light: '#e8e8e8',
  dark: '#2a2a2a',
  carmine: '#d6273d',
  softblue: '#6fb3e0',
  darkmoon: '#3c5d7a',
  darkviolet: '#7b68ee',
  greenmist: '#3cb371',
  contrast: '#ff00ff'
};

function materialEntries(): ThemeCatalogEntry[] {
  const entries: ThemeCatalogEntry[] = [];
  for (const accent of MATERIAL_ACCENTS) {
    for (const mode of ['light', 'dark'] as const) {
      entries.push({
        themeId: `material.${accent}.${mode}`,
        cssFile: `dx.material.${accent}.${mode}.css`,
        family: 'material',
        label: MATERIAL_LABELS[accent],
        swatchColor: MATERIAL_SWATCHES[accent],
        supportsToolbarMode: true
      });
    }
  }
  return entries;
}

function fluentEntries(): ThemeCatalogEntry[] {
  const entries: ThemeCatalogEntry[] = [];
  for (const variant of FLUENT_VARIANTS) {
    if (variant === 'red-storm') {
      entries.push({
        themeId: 'fluent.red-storm.dark',
        cssFile: 'dx.fluent.red-storm.dark.css',
        family: 'fluent',
        label: FLUENT_LABELS[variant],
        swatchColor: FLUENT_SWATCHES[variant],
        supportsToolbarMode: false
      });
      continue;
    }
    for (const mode of ['light', 'dark'] as const) {
      entries.push({
        themeId: `fluent.${variant}.${mode}`,
        cssFile: `dx.fluent.${variant}.${mode}.css`,
        family: 'fluent',
        label: FLUENT_LABELS[variant],
        swatchColor: FLUENT_SWATCHES[variant],
        supportsToolbarMode: true
      });
      entries.push({
        themeId: `fluent.${variant}.${mode}.compact`,
        cssFile: `dx.fluent.${variant}.${mode}.compact.css`,
        family: 'fluent',
        label: FLUENT_LABELS[variant],
        swatchColor: FLUENT_SWATCHES[variant],
        supportsToolbarMode: true
      });
    }
  }
  return entries;
}

function genericEntries(): ThemeCatalogEntry[] {
  const entries: ThemeCatalogEntry[] = [];
  for (const palette of GENERIC_PALETTES) {
    entries.push({
      themeId: `generic.${palette}`,
      cssFile: `dx.${palette}.css`,
      family: 'generic',
      label: GENERIC_LABELS[palette],
      swatchColor: GENERIC_SWATCHES[palette],
      supportsToolbarMode: palette === 'light' || palette === 'dark'
    });
    entries.push({
      themeId: `generic.${palette}.compact`,
      cssFile: `dx.${palette}.compact.css`,
      family: 'generic',
      label: GENERIC_LABELS[palette],
      swatchColor: GENERIC_SWATCHES[palette],
      supportsToolbarMode: palette === 'light' || palette === 'dark'
    });
  }
  return entries;
}

export const THEME_CATALOG: ThemeCatalogEntry[] = [
  ...materialEntries(),
  ...genericEntries(),
  ...fluentEntries()
];

const STOCK_THEME_CSS_FILES: string[] = THEME_CATALOG.map((e) => e.cssFile).filter(
  (file) => !(CUSTOM_THEME_CSS_FILES as readonly string[]).includes(file)
);

export const THEME_CSS_FILES: string[] = [
  ...STOCK_THEME_CSS_FILES,
  ...CUSTOM_THEME_CSS_FILES
];

export const MATERIAL_THEME_OPTIONS = MATERIAL_ACCENTS.map((accent) => ({
  accent,
  label: MATERIAL_LABELS[accent],
  swatchColor: MATERIAL_SWATCHES[accent]
}));

export const FLUENT_THEME_OPTIONS = FLUENT_VARIANTS.map((variant) => ({
  variant,
  label: FLUENT_LABELS[variant],
  swatchColor: FLUENT_SWATCHES[variant]
}));

export const GENERIC_THEME_OPTIONS = GENERIC_PALETTES.map((palette) => ({
  palette,
  label: GENERIC_LABELS[palette],
  swatchColor: GENERIC_SWATCHES[palette],
  supportsToolbarMode: palette === 'light' || palette === 'dark'
}));

export const DEFAULT_THEME_PREFERENCES: ThemePreferences = {
  family: 'material',
  materialAccent: 'blue',
  genericPalette: 'light',
  genericCompact: false,
  fluentVariant: 'blue',
  fluentCompact: false,
  mode: 'light'
};
